import type {
  GameControllerCreateData,
  GameControllerCreateResponse,
  GameControllerGetGameData,
  GameControllerGetGameResponse,
  GameControllerGetGamesData,
  GameControllerGetGamesResponse,
  GameControllerRemoveResponse,
  GameControllerUpdateData,
  GameControllerUpdateResponse,
} from '~/api';
import {
  gameControllerCreate,
  gameControllerGetGame,
  gameControllerGetGames,
  gameControllerRemove,
  gameControllerUpdate,
} from '~/api';
import { heyApiQuery } from '~/utils/api';

import api from '..';

export const games = api.injectEndpoints({
  endpoints: builder => ({
    getGames: builder.query<GameControllerGetGamesResponse, GameControllerGetGamesData['query']>({
      queryFn: params => heyApiQuery(() => gameControllerGetGames({ query: params })),
      providesTags: result => (result?.success ? [{ type: 'GAME', id: 'ALL' }] : [{ type: 'GAME', id: 'ALL' }]),
    }),
    getGame: builder.query<GameControllerGetGameResponse, GameControllerGetGameData['path']>({
      queryFn: params => heyApiQuery(() => gameControllerGetGame({ path: params })),
      providesTags: result => (result?.success ? [{ type: 'GAME', id: result.data.id }] : []),
    }),
    createGame: builder.mutation<GameControllerCreateResponse, GameControllerCreateData['body']>({
      queryFn: params => heyApiQuery(() => gameControllerCreate({ body: params })),
      invalidatesTags: result => (result?.success ? [{ type: 'GAME', id: 'ALL' }] : []),
    }),
    updateGame: builder.mutation<
      GameControllerUpdateResponse,
      {
        id: GameControllerUpdateData['path']['id'];
        params: GameControllerUpdateData['body'];
      }
    >({
      queryFn: ({ id, params }) => heyApiQuery(() => gameControllerUpdate({ path: { id }, body: params })),
      async onQueryStarted({ id, params }, api) {
        const { dispatch, queryFulfilled } = api;
        const state = api.getState();

        const patches: { undo: () => void }[] = [];

        const queries = games.util.selectInvalidatedBy(state, [{ type: 'GAME', id: 'ALL' }]);

        for (const query of queries) {
          if (query.endpointName !== 'getGames') continue;

          const patch = dispatch(
            games.util.updateQueryData('getGames', query.originalArgs as GameControllerGetGamesData['query'], draft => {
              const game = draft.data.find(g => g.id === Number(id));
              if (game) {
                Object.assign(game, params);
              }
            })
          );

          patches.push(patch);
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(p => {
            p.undo();
          });
        }
      },
      invalidatesTags: (result, _error, params) => {
        return result?.success ? [{ type: 'GAME', id: params.id }] : [];
      },
    }),
    deleteGame: builder.mutation<GameControllerRemoveResponse, number>({
      queryFn: id => heyApiQuery(() => gameControllerRemove({ path: { id: `${id}` } })),
      invalidatesTags: () => [{ type: 'GAME', id: 'ALL' }],
    }),
  }),
  overrideExisting: false,
});
